import React from 'react';
import styled from 'styled-components';
import type {
  SubmitInputResponseArgs,
  Turn,
} from '../../../hooks/useWorkspace';
import TurnPromptHeader from './TurnPromptHeader';
import TurnThinkingStrip from './TurnThinkingStrip';
import TurnActivityStrip from './TurnActivityStrip';
import AssistantResponse from './AssistantResponse';
import TurnFooter from './TurnFooter';
import AgentInputRequestCard from './AgentInputRequestCard';

const Article = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 0 1.25rem;
  position: relative;

  /* Reveal hover-only footer actions */
  &:hover .turn-footer,
  &:focus-within .turn-footer {
    opacity: 1;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--border);
  margin-top: 0.5rem;
  opacity: 0.6;
`;

interface WorkspaceTurnProps {
  turn: Turn;
  /** True iff this turn owns the currently-pending input request. */
  isPendingInput: boolean;
  isLast: boolean;
  onSubmitInputResponse: (args: SubmitInputResponseArgs) => void;
}

/**
 * One turn of the Cursor-style conversation document: prompt header, activity
 * strip, assistant response, optional agent_input_request card, and a
 * hover-visible footer. A hairline divider separates turns.
 */
const WorkspaceTurn: React.FC<WorkspaceTurnProps> = ({
  turn,
  isPendingInput,
  isLast,
  onSubmitInputResponse,
}) => {
  const isFollowUp = Boolean(turn.userPrompt.isFollowUp);

  return (
    <Article
      role="article"
      aria-label={isFollowUp ? 'Follow-up work' : `Turn ${turn.index + 1}`}
      data-turn-id={turn.id}
      id={`turn-${turn.id}`}
      data-followup={isFollowUp ? 'true' : undefined}
    >
      {!isFollowUp && (
        <TurnPromptHeader
          userPrompt={turn.userPrompt}
          turnIndex={turn.index}
        />
      )}

      <TurnThinkingStrip thinking={turn.thinking} status={turn.status} />

      <TurnActivityStrip
        activities={turn.activities}
        status={turn.status}
        startTime={turn.startTime}
        durationMs={turn.durationMs}
      />

      <AssistantResponse
        content={turn.assistantContent}
        status={turn.status}
        errorSummary={turn.errorSummary}
      />

      {turn.inputRequest && (
        <AgentInputRequestCard
          inputRequest={turn.inputRequest}
          isPending={isPendingInput}
          onSubmit={onSubmitInputResponse}
        />
      )}

      <TurnFooter
        turnId={turn.id}
        assistantContent={turn.assistantContent}
        status={turn.status}
      />

      {!isLast && <Divider aria-hidden="true" />}
    </Article>
  );
};

export default WorkspaceTurn;
